/** GPU Chromatic Aberration — fragment-shader port of
 *  `lib/filters/chromatic-aberration.ts`.
 *
 *  Per-pixel three-tap sample: R from a slightly-outward radial
 *  offset, G at the source position, B from a slightly-inward
 *  offset. Offset magnitude grows linearly with radial distance,
 *  matching the JS algorithm and the real-world optics it
 *  simulates.
 *
 *  Nearest-neighbor sampling matches the JS's `Math.round` +
 *  bounds-clamp pattern. We compute the integer pixel index with
 *  `floor(... + 0.5)` (round-half-up for non-negative values —
 *  same as JS for these positive-or-clamped sample positions) and
 *  convert to UV explicitly so texture() with NEAREST filtering
 *  returns the same texel JS picks via src[rYi * w + rXi]. */

import { applyIdentityCopy } from '../identity-copy';
import type { ChromaticAberrationValue } from '../../filters/chromatic-aberration';
import { applyShader } from '../runtime';

const CHROMATIC_ABERRATION_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;        // (width, height)
uniform vec2 u_center;      // ((w-1)/2, (h-1)/2)
uniform float u_halfDiag;
uniform float u_amount;     // raw pixel-magnitude (non-negative; JS rejects negative)
out vec4 outColor;

void main() {
  // Pixel coord in source-canvas space, j=0 at top. v_uv = (i+0.5)/dims
  // for NEAREST filtering; subtracting 0.5 recovers the integer index
  // the JS loop iterates on.
  vec2 px = v_uv * u_dims - 0.5;
  vec2 d = px - u_center;
  float r = length(d);
  // Avoid divide-by-zero at the exact center. JS does the same guard.
  vec2 n = r > 0.0 ? d / r : vec2(0.0);
  float t = r / u_halfDiag;
  float offsetPx = u_amount * t;

  // R: sample outward (away from center). Round-then-clamp matches JS.
  vec2 rIdx = clamp(floor(px + n * offsetPx + 0.5), vec2(0.0), u_dims - 1.0);
  // B: sample inward (toward center).
  vec2 bIdx = clamp(floor(px - n * offsetPx + 0.5), vec2(0.0), u_dims - 1.0);

  vec4 rSrc = texture(u_source, (rIdx + 0.5) / u_dims);
  vec4 gSrc = texture(u_source, v_uv);
  vec4 bSrc = texture(u_source, (bIdx + 0.5) / u_dims);

  // Output: R from outward, G + A unchanged, B from inward.
  outColor = vec4(rSrc.r, gSrc.g, bSrc.b, gSrc.a);
}
`;

export function applyChromaticAberrationOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: ChromaticAberrationValue,
): boolean {
  // JS clamps to non-negative + short-circuits at 0 — mirror both.
  const amount = Math.max(0, value.amount);
  if (amount === 0) return applyIdentityCopy(source, target);
  const w = source.width;
  const h = source.height;
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;
  const halfDiag = Math.sqrt(cx * cx + cy * cy);
  return applyShader({
    source,
    target,
    fragShaderSrc: CHROMATIC_ABERRATION_FRAG,
    uniforms: {
      u_dims: { type: 'vec2', value: [w, h] },
      u_center: { type: 'vec2', value: [cx, cy] },
      u_halfDiag: { type: 'float', value: halfDiag },
      u_amount: { type: 'float', value: amount },
    },
  });
}
